// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type LatestPricesResp } from '@shared/data-access/interfaces/api/commons';
import { type BrokerConfiguration } from '@shared/data-access/interfaces/api/brokerage/commons';
import { type TradeDetails } from '@shared/data-access/interfaces/redux/trades';
import { selectBrokerApiParams } from '@advisor-app/store/slices/broker/broker-selectors';
import { type AppState } from '@advisor-app/store/store';
import { arrayToMap } from '@advisor-app/features/utils/array-to-map';
import {
  type QueryReturnValue,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { type TradeParams } from '@advisor-app/store/slices/trader';
import { normalizeContractsNumber } from '@advisor-app/features/operations/utils/normalize-contracts';
import { type TradesToOrdersResp } from '@shared/data-access/interfaces/api/ai/trader';
import { brokerageApi } from '@shared/data-access/api-base/citec-brokerage-api';
import { OperationType } from '@shared/data-access/interfaces/operation-type';
import { SharedTagTypes } from '@shared/data-access/utils/constants';

export const equityApi = brokerageApi.injectEndpoints({
  endpoints: build => ({
    prices: build.query<
      LatestPricesResp[],
      { params: BrokerConfiguration; tickers: string[] }
    >({
      query: ({ params, tickers }) => ({
        url: '/v2/equity/prices/',
        method: 'POST',
        params: params,
        body: tickers,
      }),
      extraOptions: {
        includeCurrency: true,
        useTrackPromise: true,
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ ticker }) => ({
                type: 'Equity' as const,
                id: ticker,
              })),
              { type: 'Equity', id: 'Prices' },
              SharedTagTypes.CurrencyConversion,
            ]
          : [{ type: 'Equity', id: 'Prices' }, SharedTagTypes.CurrencyConversion],
    }),
    citecPrice: build.query<LatestPricesResp[], string[]>({
      query: tickers => ({
        url: '/v2/equity/citec/price/',
        method: 'POST',
        body: tickers,
      }),
      extraOptions: {
        includeCurrency: true,
        useTrackPromise: true,
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ ticker }) => ({
                type: 'Equity' as const,
                id: ticker,
              })),
              { type: 'Equity', id: 'CitecPrices' },
              SharedTagTypes.CurrencyConversion,
            ]
          : [
              { type: 'Equity', id: 'CitecPrices' },
              SharedTagTypes.CurrencyConversion,
            ],
    }),
    getLatestPrices: build.query<TradeDetails[], TradeDetails[]>({
      queryFn: async (trades, queryApi, _extraOptions, fetchWithBQ) => {
        const broker = selectBrokerApiParams(queryApi.getState() as AppState);

        const tickers = trades.map(({ ticker }) => ticker);

        /* istanbul ignore next */
        const latestPricesResponse = (await fetchWithBQ({
          url: broker.brokerConnected
            ? '/v2/equity/prices/'
            : '/v2/equity/citec/price/',
          method: 'POST',
          body: tickers,
          params: broker.brokerConnected
            ? {
                broker_key: broker.brokerConnected.toLowerCase(),
                type_account: broker.accountTypeSelect,
              }
            : undefined,
        })) as QueryReturnValue<
          LatestPricesResp[],
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        /* istanbul ignore if */
        if (latestPricesResponse.error) {
          return { error: latestPricesResponse.error as FetchBaseQueryError };
        }

        /* istanbul ignore next */
        const latestPrices = latestPricesResponse.data ?? [];

        const prices = arrayToMap(latestPrices, 'ticker');
        /* istanbul ignore next */
        const traderDetails = trades.map(trade => {
          const asset = prices.get(trade.ticker);
          const price = (asset?.price as number) ?? 0.000001;
          const localPrice = asset?.local_currency_values?.price as number;
          const localVolume = trade.local_currency_values?.volume as number;
          const isAmountInput = trade.inputType === 'amount';
          const contracts = localPrice
            ? isAmountInput
              ? localVolume / localPrice
              : trade.contracts_number
            : trade.contracts_number;
          const expectedLocalVolume = localPrice
            ? isAmountInput
              ? localVolume
              : contracts * localPrice
            : contracts * price;

          return {
            ...trade,
            expected_volume: price * contracts,
            expected_price: price,
            contracts_number: contracts,
            local_currency_values: {
              volume: localVolume,
              currency: asset?.local_currency_values?.currency ?? '',
              price: localPrice,
              expected_volume: expectedLocalVolume,
              delta_volume: localPrice ? expectedLocalVolume - localVolume : 0,
            },
            transfer_target: trade.transfer_target,
          };
        });

        return {
          data: traderDetails,
        };
      },
      extraOptions: {
        includeCurrency: true,
        useTrackPromise: true,
      },
      providesTags: [
        { type: 'Equity', id: 'LatestPrices' },
        SharedTagTypes.CurrencyConversion,
      ],
    }),
    getOrderPrices: build.query<TradeDetails[], TradeParams[]>({
      queryFn: async (trades, queryApi, _extraOptions, fetchWithBQ) => {
        const broker = selectBrokerApiParams(queryApi.getState() as AppState);

        const tickers = trades.flatMap(({ ticker, transfer_target, isin }) =>
          [ticker, transfer_target, isin].filter(Boolean),
        );

        /* istanbul ignore next */
        const latestPricesResponse = (await fetchWithBQ({
          url: broker.brokerConnected
            ? '/v2/equity/prices/'
            : '/v2/equity/citec/price/',
          method: 'POST',
          body: tickers,
          params: broker.brokerConnected
            ? {
                broker_key: broker.brokerConnected.toLowerCase(),
                type_account: broker.accountTypeSelect,
              }
            : undefined,
        })) as QueryReturnValue<
          LatestPricesResp[],
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        /* istanbul ignore if */
        if (latestPricesResponse.error) {
          return { error: latestPricesResponse.error as FetchBaseQueryError };
        }

        /* istanbul ignore next */
        const latestPrices = latestPricesResponse.data ?? [];

        const normalizeContracts = normalizeContractsNumber;

        const computedOrders: TradesToOrdersResp[] = trades
          .filter(
            (
              trade,
            ): trade is TradeParams & {
              operation: Exclude<
                TradeParams['operation'],
                typeof OperationType.TRANSFER
              >;
            } => trade.operation !== OperationType.TRANSFER,
          )
          .map(trade => {
            const latestPrice = latestPrices.find(
              price => price.ticker === trade.ticker,
            );
            const priceVal = latestPrice?.price ?? 1;
            const localPriceVal =
              latestPrice?.local_currency_values?.price ?? priceVal;
            const volumeConverted = (trade.volume * localPriceVal) / priceVal;
            const fallbackContracts =
              localPriceVal > 0 ? volumeConverted / localPriceVal : 0;
            const isAmountInput = trade.inputType === 'amount';
            const contracts = isAmountInput
              ? normalizeContracts(fallbackContracts, trade.columnName?.asset_type)
              : normalizeContracts(
                  trade.contracts_number ?? 0,
                  trade.columnName?.asset_type,
                );
            const expectedVolume = contracts * localPriceVal;

            return {
              operation: trade.operation,
              realized_capital_gains: trade.realized_capital_gains,
              ticker: trade.ticker,
              unrealized_capital_gains: trade.unrealized_capital_gains,
              volume: volumeConverted,
              contracts_number: contracts,
              isin: trade.isin ?? undefined,
              expected_price: localPriceVal,
              expected_volume: expectedVolume,
              delta_volume: expectedVolume - volumeConverted,
            };
          });

        /* istanbul ignore next */
        const transferTrades = trades
          .filter(({ operation }) => operation === OperationType.TRANSFER)
          .map(trade => {
            const sourcePrice =
              latestPrices.find(price =>
                [trade.ticker, trade.isin].includes(price.ticker),
              )?.price ?? 1;

            const isAmountInput = trade.inputType === 'amount';
            const rawQuantity = isAmountInput
              ? trade.volume / sourcePrice
              : trade.contracts_number ?? trade.volume / sourcePrice;
            const quantity = normalizeContracts(
              rawQuantity,
              trade.to_asset_details?.asset_type ?? trade.columnName?.asset_type,
            );
            const expectedVolume = isAmountInput
              ? trade.volume
              : quantity * sourcePrice;

            return {
              contracts_number: quantity,
              delta_volume: 0,
              expected_price: sourcePrice,
              expected_volume: expectedVolume,
              operation: OperationType.TRANSFER as any,
              realized_capital_gains: trade.realized_capital_gains,
              unrealized_capital_gains: trade.unrealized_capital_gains,
              ticker: trade.transfer_target,
              isin: trade.to_isin,
              volume: expectedVolume,
              from_isin: trade.isin,
              from_ticker: trade.ticker,
            };
          }) as Array<
          TradesToOrdersResp & { from_isin?: string; from_ticker?: string }
        >;

        /* istanbul ignore next */
        const tradesToOrders = computedOrders.concat(transferTrades) as Array<
          TradesToOrdersResp & { from_isin?: string; from_ticker?: string }
        >;

        /* istanbul ignore next */
        const traderDetails: TradeDetails[] = tradesToOrders.map(trade => {
          const sourceTrade = trades.find(
            t =>
              (t.ticker === trade.ticker &&
                t.operation === trade.operation &&
                t.isin === trade.isin) ||
              (trade.from_isin === t.isin &&
                trade.from_ticker === t.ticker &&
                t.operation === trade.operation),
          )!;

          const isTransfer =
            sourceTrade.operation === OperationType.TRANSFER &&
            sourceTrade.transfer_target;

          const sourceTradeAny = sourceTrade as typeof sourceTrade & {
            holding_id?: string | null;
            name?: string | null;
            date?: string | null;
            to_isin?: string;
            to_asset_details?: {
              name?: string;
              asset_type?: string;
              url_logo?: string;
              url_morningstar?: string;
              dfi_url?: string | null;
            };
          };

          const priceInfoForTransfer = latestPrices.find(
            p => p.ticker === sourceTrade.ticker,
          );
          const priceInfoForTrade = latestPrices.find(
            p => p.ticker === trade.ticker,
          );

          const transferContext = (() => {
            const priceSource = priceInfoForTransfer;
            const currencyRate =
              (priceSource?.local_currency_values?.price || 1) /
              (priceSource?.price || 1);
            const fromPrice =
              priceSource?.local_currency_values?.price ?? priceSource?.price ?? 0;
            const volume = (trade.contracts_number ?? 0) * fromPrice;
            return {
              ticker: sourceTrade.ticker!,
              isin: sourceTrade.isin,
              operation: 'TRANSFER' as const,
              priceTicker: sourceTrade.ticker!,
              currency:
                priceSource?.local_currency_values?.currency ??
                priceSource?.currency ??
                '',
              expected_volume: volume / currencyRate,
              volume: volume / currencyRate,
              local_currency_values: {
                ...priceSource?.local_currency_values,
                expected_volume: volume / currencyRate,
                volume: volume / currencyRate,
                delta_volume: trade.delta_volume,
              },
              transferExtras: {
                ...(sourceTradeAny.to_isin !== null && {
                  to_isin: sourceTradeAny.to_isin,
                }),
                ...(sourceTradeAny.to_asset_details !== null && {
                  to_asset_details: sourceTradeAny.to_asset_details,
                }),
              },
            };
          })();

          const defaultContext = (() => {
            const priceSource = priceInfoForTrade;
            const currencyRate =
              (priceSource?.local_currency_values?.price || 1) /
              (priceSource?.price || 1);
            return {
              ticker: trade.ticker,
              isin: trade.isin,
              operation: trade.operation,
              priceTicker: trade.ticker,
              currency: priceSource?.currency ?? '',
              expected_volume: trade.expected_volume / currencyRate,
              volume: trade.volume / currencyRate,
              local_currency_values: {
                ...priceSource?.local_currency_values,
                expected_volume: trade.expected_volume / currencyRate,
                volume: trade.volume / currencyRate,
                delta_volume: trade.delta_volume,
              },
              transferExtras: {},
            };
          })();

          const ctx = isTransfer ? transferContext : defaultContext;
          const expectedPrice =
            latestPrices.find(p => p.ticker === ctx.priceTicker)?.price ?? 0;

          return {
            ...trade,
            holding_id: sourceTradeAny.holding_id,
            name: sourceTrade.columnName?.name ?? sourceTradeAny.name ?? null,
            date: sourceTradeAny.date,
            ticker: ctx.ticker,
            isin: ctx.isin,
            operation: ctx.operation,
            expected_volume: ctx.expected_volume,
            volume: ctx.volume,
            expected_price: expectedPrice,
            exchange: sourceTrade.exchange,
            currency: ctx.currency,
            columnName: {
              ...sourceTrade.columnName,
              name:
                sourceTrade.columnName?.name ?? sourceTradeAny.name ?? trade.ticker,
            },
            gain_loss: [
              trade.unrealized_capital_gains,
              trade.unrealized_capital_gains / trade.volume,
            ],
            local_currency_values: ctx.local_currency_values,
            account_id: sourceTrade.account_id,
            from_iban: sourceTrade.from_iban,
            to_iban: sourceTrade.to_iban,
            institution_id: sourceTrade.institution_id,
            institution_name: sourceTrade.institution_name,
            transfer_target: sourceTrade.transfer_target,
            inputType: sourceTrade.inputType,
            ...ctx.transferExtras,
          } as TradeDetails;
        });

        return {
          data: traderDetails,
        };
      },
      providesTags: [
        { type: 'Equity', id: 'OrderPrices' },
        SharedTagTypes.CurrencyConversion,
      ],
      keepUnusedDataFor: 10000,
      extraOptions: {
        includeCurrency: true,
        useTrackPromise: true,
      },
    }),
  }),
});
